import { ITableRow } from 'types'

export interface IDraggedRow {
  rowId: string
  rowNumber: number
  // How far down the row the pointer grabbed it, so the lifted row stays under the cursor where it was picked up.
  grabOffset: number
  // Where the row will land. Null until the drag has moved; when grouped by a column the target is tracked separately.
  slot: number | null
  // Dropped and waiting on the server: the row is settled into its slot until the new order arrives.
  dropped: boolean
}

/**
 * The row number a dragged row would occupy if it were dropped on `overRowNumber` right now — the
 * slot behind the drop-preview ghost shown when the view is grouped by a column.
 *
 * `overRowNumber` is the row currently under the cursor, or null when the drag hasn't reached one,
 * in which case the ghost marks the row where it already is — which is what puts the ghost on screen
 * from the moment the drag starts.
 *
 * A normal drop places the row immediately before the row it was dropped on (`updateRowPosition`
 * sends that row as `nextRowPublicId`), so dragging down lands one slot above the target once the
 * rows in between close up. The transposed view reorders the underlying columns with a splice
 * instead, which puts the dragged row exactly where the target was in either direction.
 */
export const computeRowDropSlot = (
  draggedRowNumber: number,
  overRowNumber: number | null,
  isTransposed: boolean
): number => {
  if (overRowNumber === null || overRowNumber === draggedRowNumber) return draggedRowNumber
  if (isTransposed || overRowNumber < draggedRowNumber) return overRowNumber
  return overRowNumber - 1
}

/**
 * The slot a lifted row lands in, from where its middle is on screen. `centerY` is measured from the
 * top of the first row, so slot n spans [n, n + 1) row heights.
 *
 * Read from the rows' resting positions rather than whichever row is under the cursor: the rows slide
 * out of the way as the slot changes, so hit-testing them would flip the slot back and forth.
 */
export const computeLiveRowDropSlot = (centerY: number, rowHeight: number, totalRows: number): number =>
  Math.min(Math.max(Math.floor(centerY / rowHeight), 0), totalRows - 1)

/**
 * How many slots a row slides while another is being dragged: rows between the dragged row and its
 * landing slot close up the space it left and open a gap where it will land.
 */
export const computeRowDragShift = (rowNumber: number, draggedRowNumber: number, slot: number): number => {
  if (slot < draggedRowNumber && rowNumber >= slot && rowNumber < draggedRowNumber) return 1
  if (slot > draggedRowNumber && rowNumber > draggedRowNumber && rowNumber <= slot) return -1
  return 0
}

export interface IRowMoveTarget {
  // Display position of the row the move is saved against; a filtered view counts only the rows it shows.
  index: number
  placement: 'before' | 'after'
}

/**
 * The row a live drop is saved against. A row normally goes in before the row that ends up below it;
 * landing in the last slot leaves nothing below, so it goes in after the last row instead. The
 * transposed view splices, so it takes the place of the slot's row in either direction.
 */
export const getRowMoveTarget = (
  draggedRowNumber: number,
  slot: number,
  totalRows: number,
  isTransposed: boolean
): IRowMoveTarget => {
  if (isTransposed || slot < draggedRowNumber) return { index: slot, placement: 'before' }
  if (slot + 1 < totalRows) return { index: slot + 1, placement: 'before' }
  return { index: slot, placement: 'after' }
}

/**
 * A sort order just below the next whole number above `sortOrder`, for placing a row after the last
 * one — the server can only place a row before another, and there is none below the last row.
 *
 * Stays short of the whole number because new rows take their sort order from an integer sequence
 * that a move doesn't advance: a whole number above the last row could be handed straight to the next
 * row someone adds, and sort orders are unique.
 */
export const getSortOrderAfter = (sortOrder: number): number => (sortOrder + Math.floor(sortOrder) + 1) / 2

/** The row at a display position, skipping rows a filter hides. */
export const getDisplayRowAt = (
  rows: ITableRow[],
  visibleBitmap: Uint8Array | null | undefined,
  index: number
): ITableRow | undefined => {
  if (!visibleBitmap) return rows[index]
  let visibleIndex = 0
  for (let i = 0; i < rows.length; i++) {
    if (!visibleBitmap[i]) continue
    if (visibleIndex === index) return rows[i]
    visibleIndex++
  }
  return undefined
}
